import React, { useState, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Upload, FileText, FileSpreadsheet, FileType2, AlertCircle, CheckCircle2, Loader2, Download, X, AlertTriangle } from "lucide-react";
import { hrImportPreview, hrImportConfirm, hrImportTemplateUrl, formatApiError, getStoredToken } from "../lib/api";

const CONTRACT_LABELS = {
    tempo_indeterminato: "T.Indeterminato",
    tempo_determinato: "T.Determinato",
    somministrazione: "Somministrazione",
    apprendistato: "Apprendistato",
    collaborazione: "Collaborazione",
    stage: "Stage",
};

export default function HRImportDialog({ open, onOpenChange, clientId, onImported }) {
    const [step, setStep] = useState("upload"); // upload | preview | done
    const [dragActive, setDragActive] = useState(false);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [selectedRows, setSelectedRows] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [confirmResult, setConfirmResult] = useState(null);
    const inputRef = useRef(null);

    const reset = () => {
        setStep("upload"); setFile(null); setPreview(null);
        setSelectedRows({}); setLoading(false); setError(null); setConfirmResult(null);
    };

    const handleClose = (v) => {
        if (!v) reset();
        onOpenChange(v);
    };

    const uploadFile = async (f) => {
        setFile(f);
        setLoading(true);
        setError(null);
        try {
            const p = await hrImportPreview(clientId, f);
            setPreview(p);
            // Select all non-duplicate rows by default
            const sel = {};
            p.rows.forEach((r, i) => { sel[i] = !r.duplicate; });
            setSelectedRows(sel);
            setStep("preview");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const handleDrag = useCallback((e) => {
        e.preventDefault(); e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
        else if (e.type === "dragleave") setDragActive(false);
    }, []);
    const handleDrop = useCallback((e) => {
        e.preventDefault(); e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) uploadFile(e.dataTransfer.files[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    const handleFileInput = (e) => {
        if (e.target.files && e.target.files[0]) uploadFile(e.target.files[0]);
    };

    const toggleRow = (i) => setSelectedRows((s) => ({ ...s, [i]: !s[i] }));
    const toggleAll = (v) => {
        const sel = {};
        preview?.rows.forEach((_, i) => { sel[i] = v; });
        setSelectedRows(sel);
    };
    const selectedCount = Object.values(selectedRows).filter(Boolean).length;

    const handleConfirm = async () => {
        if (selectedCount === 0) {
            setError("Nessuna riga selezionata");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const employees = preview.rows.filter((_, i) => selectedRows[i]);
            const res = await hrImportConfirm(clientId, employees);
            setConfirmResult(res);
            setStep("done");
            onImported?.();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const iconFor = (fname) => {
        const s = (fname || "").toLowerCase();
        if (s.endsWith(".pdf")) return <FileType2 size={18} className="text-red-600" />;
        if (s.endsWith(".xlsx") || s.endsWith(".xls")) return <FileSpreadsheet size={18} className="text-green-600" />;
        return <FileText size={18} className="text-blue-600" />;
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="hr-import-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-navy text-2xl flex items-center gap-2">
                        <Upload size={22} /> Importa Anagrafica Dipendenti
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500">
                        Carica un file CSV, Excel (.xlsx) o PDF (UNILAV/contratti) per popolare rapidamente l'anagrafica del cliente. L'AI riconosce automaticamente colonne italiane comuni.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto -mx-6 px-6">
                    {step === "upload" && (
                        <div className="py-4">
                            <div
                                onDragEnter={handleDrag}
                                onDragOver={handleDrag}
                                onDragLeave={handleDrag}
                                onDrop={handleDrop}
                                onClick={() => inputRef.current?.click()}
                                className={`relative border-2 border-dashed p-12 text-center transition-colors cursor-pointer ${
                                    dragActive ? "border-gold bg-gold/5" : "border-slate-300 hover:border-gold hover:bg-slate-50"
                                }`}
                                data-testid="dropzone"
                            >
                                <input
                                    ref={inputRef}
                                    type="file"
                                    accept=".csv,.xlsx,.xls,.pdf,.txt"
                                    onChange={handleFileInput}
                                    className="hidden"
                                    data-testid="file-input"
                                />
                                <Upload size={40} className="mx-auto text-slate-400 mb-4" />
                                <div className="font-serif text-navy text-xl mb-2">Trascina il file qui</div>
                                <div className="text-sm text-slate-600 mb-4">oppure <span className="text-gold-deep font-semibold">clicca per selezionare</span></div>
                                <div className="text-xs text-slate-500">Formati supportati: CSV · Excel (.xlsx) · PDF (UNILAV, contratti)</div>
                            </div>

                            {loading && (
                                <div className="mt-6 p-4 bg-slate-50 border border-slate-200 flex items-center gap-3 text-sm">
                                    <Loader2 size={16} className="animate-spin text-gold-deep" />
                                    <span>Analisi del file{file?.name?.toLowerCase().endsWith('.pdf') ? ' con AI (Claude)' : ''} in corso...</span>
                                </div>
                            )}
                            {error && (
                                <div className="mt-6 p-4 border border-red-300 bg-red-50 text-red-700 text-sm flex items-start gap-2" data-testid="import-error">
                                    <AlertCircle size={16} className="mt-0.5" /> {error}
                                </div>
                            )}

                            <div className="mt-6 p-5 bg-slate-50 border border-slate-200">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-600 font-bold mb-2">Suggerimento</div>
                                <div className="text-sm text-slate-700 mb-3">
                                    Per un import CSV/Excel affidabile, usa il nostro <strong>template</strong> con le colonne corrette già intestate.
                                </div>
                                <a
                                    href={hrImportTemplateUrl()}
                                    onClick={async (e) => {
                                        // Auth via header requires blob download
                                        e.preventDefault();
                                        const token = getStoredToken();
                                        try {
                                            const res = await fetch(hrImportTemplateUrl(), {
                                                headers: { Authorization: `Bearer ${token}` },
                                            });
                                            const blob = await res.blob();
                                            const url = URL.createObjectURL(blob);
                                            const a = document.createElement("a");
                                            a.href = url;
                                            a.download = "template-import-dipendenti.csv";
                                            a.click();
                                            URL.revokeObjectURL(url);
                                        } catch {}
                                    }}
                                    className="inline-flex items-center gap-2 px-4 py-2 border border-slate-400 text-slate-700 text-xs uppercase tracking-[0.18em] font-bold hover:border-navy hover:text-navy"
                                    data-testid="btn-template"
                                >
                                    <Download size={13} /> Scarica Template CSV
                                </a>
                            </div>
                        </div>
                    )}

                    {step === "preview" && preview && (
                        <div className="py-4">
                            <div className="mb-4 flex flex-wrap items-center gap-3 pb-3 border-b border-slate-200">
                                <div className="flex items-center gap-2 text-sm">
                                    {iconFor(file?.name)}
                                    <span className="font-semibold text-navy">{file?.name}</span>
                                    <span className="text-xs text-slate-500 uppercase font-mono">{preview.source_type}</span>
                                </div>
                                <div className="flex-1" />
                                <button onClick={reset} className="text-xs uppercase tracking-[0.18em] text-slate-500 hover:text-red-600 flex items-center gap-1">
                                    <X size={12} /> Cambia file
                                </button>
                            </div>

                            <div className="grid grid-cols-4 gap-px bg-slate-200 mb-4">
                                <StatBox label="Righe trovate" value={preview.total_rows} />
                                <StatBox label="Selezionate" value={selectedCount} highlight="gold" />
                                <StatBox label="Con anomalie" value={preview.with_anomalies} highlight={preview.with_anomalies > 0 ? "amber" : null} />
                                <StatBox label="Duplicati" value={preview.duplicates} highlight={preview.duplicates > 0 ? "red" : null} />
                            </div>

                            <div className="mb-3 flex items-center gap-3 text-sm">
                                <button onClick={() => toggleAll(true)} className="text-xs uppercase tracking-[0.15em] text-navy hover:text-gold-deep font-semibold underline-offset-4 hover:underline" data-testid="btn-select-all">
                                    Seleziona tutti
                                </button>
                                <span className="text-slate-300">·</span>
                                <button onClick={() => toggleAll(false)} className="text-xs uppercase tracking-[0.15em] text-slate-500 hover:text-navy font-semibold underline-offset-4 hover:underline">
                                    Deseleziona tutti
                                </button>
                            </div>

                            <div className="border border-slate-200 overflow-x-auto max-h-96 overflow-y-auto">
                                <table className="w-full min-w-[900px] text-sm">
                                    <thead className="sticky top-0 bg-navy text-white z-10">
                                        <tr>
                                            <th className="px-2 py-2 w-10"></th>
                                            <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.15em] font-bold">Nome</th>
                                            <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.15em] font-bold">CF</th>
                                            <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.15em] font-bold">Contratto</th>
                                            <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.15em] font-bold">Inizio</th>
                                            <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.15em] font-bold">Anomalie</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {preview.rows.map((r, i) => {
                                            const hasAnomalie = r.anomalie && r.anomalie.length > 0;
                                            const isDuplicate = r.duplicate;
                                            return (
                                                <tr key={i} className={`border-b border-slate-100 ${isDuplicate ? "bg-red-50/40" : hasAnomalie ? "bg-amber-50/40" : ""}`} data-testid={`preview-row-${i}`}>
                                                    <td className="px-2 py-2 text-center">
                                                        <input type="checkbox" checked={!!selectedRows[i]} onChange={() => toggleRow(i)} data-testid={`row-check-${i}`} />
                                                    </td>
                                                    <td className="px-3 py-2 font-medium text-navy">{r.nome_completo}</td>
                                                    <td className="px-3 py-2 font-mono text-xs text-slate-600">{r.codice_fiscale || <span className="text-red-500">—</span>}</td>
                                                    <td className="px-3 py-2">
                                                        <span className="text-xs">{CONTRACT_LABELS[r.tipologia_contratto] || r.tipologia_contratto}</span>
                                                    </td>
                                                    <td className="px-3 py-2 text-xs text-slate-600 font-mono">{r.data_inizio || <span className="text-red-500">—</span>}</td>
                                                    <td className="px-3 py-2 text-xs">
                                                        {hasAnomalie ? (
                                                            <ul className={`space-y-0.5 ${isDuplicate ? "text-red-700" : "text-amber-700"}`}>
                                                                {r.anomalie.map((a, j) => (
                                                                    <li key={j} className="flex items-start gap-1">
                                                                        <AlertTriangle size={10} className="mt-0.5 flex-shrink-0" /> {a}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        ) : (
                                                            <span className="text-green-600 flex items-center gap-1"><CheckCircle2 size={12} /> OK</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {error && (
                                <div className="mt-4 p-3 border border-red-300 bg-red-50 text-red-700 text-sm flex items-start gap-2">
                                    <AlertCircle size={14} className="mt-0.5" /> {error}
                                </div>
                            )}
                        </div>
                    )}

                    {step === "done" && (
                        <div className="py-12 text-center">
                            <CheckCircle2 size={56} className="mx-auto text-green-600 mb-4" />
                            <div className="font-serif text-navy text-2xl mb-2">Import completato</div>
                            <div className="text-slate-600">
                                <strong className="text-navy text-xl">{confirmResult?.inserted}</strong> dipendent{confirmResult?.inserted === 1 ? "e aggiunto" : "i aggiunti"} correttamente.
                            </div>
                            <div className="text-xs text-slate-500 mt-2">Le anomalie rilevate sono ora visibili nella dashboard cliente.</div>
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2 pt-4 border-t border-slate-200">
                    {step === "preview" && (
                        <>
                            <button
                                type="button"
                                onClick={reset}
                                className="px-5 py-2.5 border border-slate-300 text-slate-700 text-xs uppercase tracking-[0.2em] font-bold hover:border-slate-500"
                            >
                                Annulla
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirm}
                                disabled={loading || selectedCount === 0}
                                className="px-5 py-2.5 bg-navy text-white text-xs uppercase tracking-[0.2em] font-bold hover:bg-navy/90 disabled:opacity-50 inline-flex items-center gap-2"
                                data-testid="btn-import-confirm"
                            >
                                {loading && <Loader2 size={14} className="animate-spin" />}
                                Importa {selectedCount} dipendent{selectedCount === 1 ? "e" : "i"}
                            </button>
                        </>
                    )}
                    {step === "done" && (
                        <button
                            type="button"
                            onClick={() => handleClose(false)}
                            className="px-5 py-2.5 bg-navy text-white text-xs uppercase tracking-[0.2em] font-bold hover:bg-navy/90"
                            data-testid="btn-import-close"
                        >
                            Chiudi
                        </button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function StatBox({ label, value, highlight }) {
    const bg = highlight === "gold" ? "bg-gold/10" : highlight === "amber" ? "bg-amber-50" : highlight === "red" ? "bg-red-50" : "bg-white";
    const color = highlight === "gold" ? "text-gold-deep" : highlight === "amber" ? "text-amber-800" : highlight === "red" ? "text-red-700" : "text-navy";
    return (
        <div className={`p-3 ${bg}`}>
            <div className="text-[9px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-1">{label}</div>
            <div className={`font-serif text-2xl ${color}`}>{value}</div>
        </div>
    );
}
